/**
 * GuestShell — el armazón del portal del huésped.
 *
 * Es PÚBLICO: se llega por el enlace de la reserva, sin login de admin (§7.2).
 *
 * Lo que este componente aporta, y que ninguna pantalla del portal debe
 * repetir: el marco con las áreas seguras de iOS, la cabecera con el nombre de
 * la propiedad, la navegación entre áreas y el aviso de vista previa.
 *
 * No es una pantalla de admin con otro CSS. El huésped no tiene contexto
 * operativo: no hay badge de demo del PMS, ni vocabulario interno, ni densidad
 * de tabla. Una columna, mucho aire y tipografía tranquila.
 */
import React from "react";
import { NavLink } from "react-router-dom";
import { Info } from "@phosphor-icons/react";

import { builtAreas, PORTAL_DEMO_NOTE } from "@/data/guestPortalV2Demo";

/** Alto mínimo de todo lo que se toca. */
export const TAP = "min-h-[44px]";

/**
 * Marco del portal.
 *
 * Las áreas seguras van como clases y no como estilo en línea: el valor
 * sobrevive a jsdom y se puede comprobar en las pruebas, igual que en el resto
 * de superficies móviles del producto.
 */
export function GuestFrame({ children, withNav = false }) {
  return (
    <div
      className="dv-app-viewport bg-stone-50 text-stone-900 pt-[max(env(safe-area-inset-top),16px)] pb-[max(env(safe-area-inset-bottom),24px)]"
      data-testid="guest-frame"
    >
      <main className={`mx-auto w-full max-w-md px-4 ${withNav ? "pb-28" : ""}`}>
        {children}
      </main>
    </div>
  );
}

/**
 * Un enlace que no resuelve a ninguna reserva.
 *
 * No dice de quién era, ni si existió, ni cuándo caducó: un enlace roto no es
 * un sitio donde confirmar que una reserva existe.
 */
export function GuestLinkInvalid() {
  return (
    <GuestFrame>
      <div className="text-center py-10" data-testid="guest-link-invalid">
        <Info size={24} className="mx-auto mb-3 text-stone-400" />
        <h1 className="font-serif-dv text-xl text-stone-900">Este enlace no es válido</h1>
        <p className="text-sm text-stone-500 mt-2">
          Puede haber caducado. Escríbenos y te mandamos uno nuevo.
        </p>
      </div>
    </GuestFrame>
  );
}

export default function GuestShell({ token, area, title, subtitle, children }) {
  // Solo se navega a lo que existe. Las áreas de §7.2 que aún no están
  // construidas no se pintan: ni en gris ni como "próximamente".
  const areas = builtAreas();
  const withNav = Boolean(token) && areas.length > 1;

  return (
    <GuestFrame withNav={withNav}>
      <header className="mb-5" data-testid="guest-portal-header">
        <div className="text-[10px] tracking-[0.3em] uppercase text-stone-400">
          {title || "Tu estancia"}
        </div>
        <h1 className="font-serif-dv text-2xl text-stone-900 mt-0.5">
          {subtitle}
        </h1>
      </header>

      {children}

      <p className="text-[11px] text-stone-400 mt-8 leading-relaxed" data-testid="guest-demo-note">
        {PORTAL_DEMO_NOTE}
      </p>

      {withNav && (
        <nav
          className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-stone-200 pb-[max(env(safe-area-inset-bottom),10px)]"
          data-testid="guest-portal-nav"
        >
          <ul className="mx-auto max-w-md flex">
            {areas.map((a) => {
              const to = a.path ? `/guest/${token}/${a.path}` : `/guest/${token}`;
              const active = area === a.key;
              return (
                <li key={a.key} className="flex-1">
                  <NavLink
                    to={to}
                    end={!a.path}
                    aria-current={active ? "page" : undefined}
                    data-testid={`guest-nav-${a.key}`}
                    className={`${TAP} flex items-center justify-center px-3 pt-2 text-[13px] border-t-2 ${active
                      ? "border-stone-900 text-stone-900"
                      : "border-transparent text-stone-400"}`}
                  >
                    {a.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </GuestFrame>
  );
}
